<template>
  <div>
    <server-error :error="error" />
    <div>
      <div class="mt-2 mb-3">
        <button
          class="b-btn b-btn_type_primary"
          type="button"
          @click="exportPayments"
        >{{$t('CONTENT.export')}}</button>
      </div>
      <div class="b-payment-widget__table" v-if="paymentChartData">
        <div class="b-payment-widget__columns-group-wrapper">
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
            {{$t('CONTENT.date')}}
          </div>
          <div class="b-reports__table-header-block-wrap" v-for="(currencyGroup, index) in paymentChartData.currencyGroups">
            <div class="b-full-table__cell b-full-table__header-cell b-payment-widget__columns-group-name-title">
              {{getFullCurrency(currencyGroup.currencyRateType)}}
            </div>
            <div class="b-reports__table-header-block-inner">
              <div
                v-for="paymentGroup in currencyGroup.paymentGroups"
                :key="paymentGroup.parametersId"
                class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell"
                :title="paymentGroup.paymentName"
                :class="{'b-payment-widget__columns_type_single': currencyGroup.paymentGroups.length === 1}">
                {{ paymentGroup.paymentName }} {{paymentGroup.parametersExternalId}}
                <div class="lessor-name" v-for="lessor in paymentGroup.allLessors">{{ lessor.lessorName }} - с {{ stringToDate(lessor.startDate) }}</div>
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.result') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.discount_result') }}
              </div>
              <div
                v-for="additionalPayment in currencyGroup.additionalPayments"
                :key="additionalPayment.parametersId"
                class="b-full-table__cell b-full-table__header-cell b-full-table__header-cell--additional-payments b-reports__table-header-cell"
                :title="additionalPayment.paymentName"
                :class="{'b-payment-widget__columns_type_single': currencyGroup.additionalPayments.length === 1}">
                {{ additionalPayment.paymentName }}
              </div>
            </div>
          </div>
        </div>
        <div class="b-payment-widget__columns-content-wrap">
          <div class="b-payment-widget__column">
            <div
              class="b-full-table__cell b-payment-widget__column-cell"
              v-for="date in convertedDates"
              :key="date"
            >{{date}}</div>
            <div
              class="b-full-table__cell b-payment-widget__column-cell b-payment-widget__column-cell_bb_none b-payment-widget__column-result"
            >Итого</div>
          </div>
          <div v-for="currencyGroup in paymentChartData.currencyGroups" class="b-reports__table-block-wrap">
            <template class="" v-for="paymentGroup in currencyGroup.paymentGroups">
              <div class="b-payment-widget__column">
                <div class="b-full-table__cell b-payment-widget__column-cell" v-for="cost in paymentGroup.costs">
                  {{convertDecimalToStringForChart(cost)}}
                </div>
                <div
                  class="b-full-table__cell b-payment-widget__column-cell b-payment-widget__column-cell_bb_none b-payment-widget__column-result"
                >{{convertDecimalToStringForChart(paymentGroup.totalCost)}}</div>
              </div>
            </template>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="cost in currencyGroup.datesCosts">
                {{convertDecimalToStringForChart(cost)}}
              </div>
              <div
                class="b-full-table__cell b-payment-widget__column-cell b-payment-widget__column-cell_bb_none b-payment-widget__column-result"
              >{{convertDecimalToStringForChart(currencyGroup.totalCost)}}</div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="cost in currencyGroup.discountedDatesCosts">
                {{convertDecimalToStringForChart(cost)}}
              </div>
              <div
                class="b-full-table__cell b-payment-widget__column-cell b-payment-widget__column-cell_bb_none b-payment-widget__column-result"
              >{{convertDecimalToStringForChart(currencyGroup.totalDiscountedCost)}}</div>
            </div>
            <template class="" v-for="additionalPayment in currencyGroup.additionalPayments">
              <div class="b-payment-widget__column">
                <div class="b-full-table__cell b-payment-widget__column-cell" v-for="cost in additionalPayment.costs">
                  {{convertDecimalToStringForChart(cost)}}
                </div>
                <div
                  class="b-full-table__cell b-payment-widget__column-cell b-payment-widget__column-cell_bb_none b-payment-widget__column-result"
                >{{convertDecimalToStringForChart(additionalPayment.totalCost)}}</div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from "lodash";
import dateConverter from "@/utils/converters/date.converter";
import convertDecimalForChart from "@/utils/converters/convert.decimal.for.chart";
import PaymentsCalculationsAPI from "../../api/PaymentsCalculationsAPI";
import { mapGetters } from "vuex";
import getFullCurrency from "../../../../mixins/getFullCurrency";

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    contractId: null,
    currentVersionId: {
      required: true,
      default: String | null
    },
    isPreview: {
      default: false
    }
  },
  data() {
    return {
      error: null,
      date: null,
      paymentChartData: null
    };
  },
  mixins: [getFullCurrency],
  computed: {
    ...mapGetters(["getContractState"]),
    ...mapGetters(["getCurrentContract"]),
    ...mapGetters(["allCounterparties"]),
    isEditContract() {
      return this.getContractState;
    },
    convertedDates() {
      const dates = lodash.cloneDeep(this.paymentChartData.dates);
      for (let i = 0; i < dates.length; i++) {
        const element = dates[i];
        dates[i] = this.$d(dateConverter.convertToDate(element));
      }
      this.date = dates[0];
      return dates;
    },
    lessorComponentShares() {
      return this.getCurrentContract.assetComponents
        .flatMap(assetComponent => assetComponent.lessorComponentShares)
    },
    currentLessors() {
      return this.allCounterparties
        .filter(counterparty => this.getCurrentContract.contract.lessorCounterpartyIds.includes(counterparty.idInDB))
    },
  },
  async created() {
    await this.calculatePaymentsAsync();
  },
  watch: {
    async component() {
      await this.calculatePaymentsAsync();
    },
    async isEditContract() {
      await this.calculatePaymentsAsync();
    }
  },
  methods: {
    async calculatePaymentsAsync() {
      this.$fileLoadingManager.startLoading();
      try {
        this.error = null;
        if (this.isPreview || this.isEditContract) {
          this.paymentChartData = await new PaymentsCalculationsAPI().calculatePaymentsPreview(
            this.component
          );
        } else {
          this.paymentChartData = await new PaymentsCalculationsAPI().calculatePayments(
            this.contractId,
            this.component.idInDB,
            this.currentVersionId
          );
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.$fileLoadingManager.finishLoading();
      }
    },

    async exportPayments() {
      this.error = null;
      try {
        const api = new PaymentsCalculationsAPI();
        const response = await api.exportResultOfCalculatePayments(this.paymentChartData);

        let filename = null;
        let filenameInURI = null;

        let headerContentDisposition = response.headers['content-disposition'].split(';');
        for (let i = 0; i < headerContentDisposition.length; i++) {
          let item = headerContentDisposition[i].split('=');
          if(item[0].trim() === 'filename*') {
            filenameInURI = item[1].split("''");
            filename = decodeURIComponent(filenameInURI[1]);
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.error = e;
        console.error(e);
      }
    },
    convertDecimalToStringForChart(decimal) {
      return convertDecimalForChart(decimal);
    },
    stringToDate(str) {
      return this.$d(dateConverter.convertToDate(str))
    }
  }
};
</script>

<style>
.lessor-name {
  font-weight: 400;
  font-size: 12px;
}
</style>
